import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors } from '../brand-colors';

/**
 * Branded Navigation Bar
 *
 * Props:
 * - appName: The application name (e.g., "Profit Map", "AI Profit Engineer")
 * - screenName: Current screen name (e.g., "Dashboard", "Exit Strategy")
 * - userEmail: Current user's email address
 * - onLogout: Function to call on logout
 * - showQuickStart: Whether to show Quick-Start button
 * - showBackToQuickStart: Whether to show "Back to Quick-Start" button
 * - logoSrc: Path to logo image (optional)
 */
function BrandNavBar({
  appName = 'Profit Map',
  screenName = '',
  userEmail = '',
  onLogout,
  showQuickStart = false,
  showBackToQuickStart = false,
  logoSrc = '/images/logo-gold.png'
}) {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navStyle = {
    backgroundColor: colors.greyDarkest,
    borderBottom: `2px solid ${colors.goldLight}`,
    padding: '0.75rem 1.5rem',
    marginBottom: '0',
    position: 'sticky',
    top: 0,
    zIndex: 1000,
  };

  const logoStyle = {
    height: '36px',
    marginRight: '0.75rem',
  };

  const appNameStyle = {
    color: colors.goldLight,
    fontSize: '1.25rem',
    fontWeight: '600',
    margin: 0,
    marginRight: '2rem',
  };

  const screenNameStyle = {
    color: colors.goldLight,
    fontSize: '1.1rem',
    fontWeight: '400',
    margin: 0,
    opacity: 0.9,
  };

  const buttonBaseStyle = {
    padding: '0.4rem 1rem',
    fontSize: '0.9rem',
    fontWeight: '500',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginLeft: '0.5rem',
    transition: 'all 0.2s ease',
  };

  const goldButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: 'transparent',
    color: colors.goldLight,
    border: `1px solid ${colors.goldLight}`,
  };

  // User avatar circle style
  const avatarStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: colors.goldLight,
    color: colors.greyDarkest,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    marginLeft: '1rem',
    fontSize: '1.1rem',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    border: `2px solid ${colors.goldLight}`,
  };

  const avatarHoverStyle = {
    backgroundColor: colors.gold,
    borderColor: colors.gold,
  };

  // Dropdown menu styles
  const dropdownStyle = {
    position: 'absolute',
    top: '50px',
    right: '0',
    backgroundColor: colors.greyDarker,
    border: `1px solid ${colors.goldDark}`,
    borderRadius: '8px',
    minWidth: '220px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
    zIndex: 1001,
    overflow: 'hidden',
  };

  const dropdownHeaderStyle = {
    padding: '1rem',
    borderBottom: `1px solid ${colors.grey}`,
    color: colors.greyLight,
    fontSize: '0.85rem',
  };

  const dropdownItemStyle = {
    padding: '0.75rem 1rem',
    color: colors.goldLight,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    transition: 'background-color 0.2s ease',
    backgroundColor: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left',
    fontSize: '0.95rem',
  };

  const dropdownItemHoverStyle = {
    backgroundColor: colors.greyDark,
  };

  const logoutItemStyle = {
    ...dropdownItemStyle,
    color: colors.redLight,
    borderTop: `1px solid ${colors.grey}`,
  };

  // Get user initials from email
  const getInitials = (email) => {
    if (!email) return '?';
    const name = email.split('@')[0];
    if (name.length <= 2) return name.toUpperCase();
    return name.charAt(0).toUpperCase();
  };

  return (
    <nav style={navStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Left: Logo + App Name */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={logoSrc}
            alt="Logo"
            style={logoStyle}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <h1 style={appNameStyle}>{appName}</h1>
        </div>

        {/* Center: Screen Name */}
        <div style={{ flex: 1, textAlign: 'center' }}>
          {screenName && <span style={screenNameStyle}>{screenName}</span>}
        </div>

        {/* Right: Action Buttons + User Menu */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {showQuickStart && (
            <button
              style={goldButtonStyle}
              onClick={() => navigate('/quick-start')}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colors.goldLight;
                e.target.style.color = colors.greyDarkest;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = colors.goldLight;
              }}
            >
              Quick-Start
            </button>
          )}
          {showBackToQuickStart && (
            <button
              style={goldButtonStyle}
              onClick={() => navigate('/quick-start-dashboard')}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = colors.goldLight;
                e.target.style.color = colors.greyDarkest;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = colors.goldLight;
              }}
            >
              Quick-Start Dashboard
            </button>
          )}

          {/* User Avatar Dropdown */}
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <div
              style={avatarStyle}
              onClick={() => setDropdownOpen(!dropdownOpen)}
              onMouseEnter={(e) => Object.assign(e.target.style, avatarHoverStyle)}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = colors.goldLight;
                e.target.style.borderColor = colors.goldLight;
              }}
              title={userEmail || 'User menu'}
            >
              {getInitials(userEmail)}
            </div>

            {dropdownOpen && (
              <div style={dropdownStyle}>
                {/* User Email Header */}
                <div style={dropdownHeaderStyle}>
                  <div style={{ color: colors.goldLight, fontWeight: '500', marginBottom: '0.25rem' }}>
                    Signed in as
                  </div>
                  <div style={{ color: colors.greyLightest, fontSize: '0.9rem', wordBreak: 'break-all' }}>
                    {userEmail || 'Unknown'}
                  </div>
                </div>

                {/* Menu Items */}
                <button
                  style={dropdownItemStyle}
                  onClick={() => { navigate('/welcome'); setDropdownOpen(false); }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = colors.greyDark}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <span style={{ fontSize: '1.1rem' }}>👋</span>
                  Welcome
                </button>

                {/* Logout */}
                {onLogout && (
                  <button
                    style={logoutItemStyle}
                    onClick={() => { onLogout(); setDropdownOpen(false); }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.greyDark}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <span style={{ fontSize: '1.1rem' }}>🚪</span>
                    Logout
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default BrandNavBar;
